import { useTeam } from "@/hooks/useTeam";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Users, Info } from "lucide-react";

// Tiered pricing (SAR) — must match edge functions
const pricingTiers = [
  { min: 1, max: 9, monthlyPrice: 55, yearlyPrice: 35 },
  { min: 10, max: 49, monthlyPrice: 44, yearlyPrice: 27 },
  { min: 50, max: Infinity, monthlyPrice: 33, yearlyPrice: 19 },
];

function calculateTeamTotal(seats: number, billingCycle: "monthly" | "yearly"): number {
  let remaining = seats;
  let seatIndex = 1;
  let total = 0;

  for (const tier of pricingTiers) {
    if (remaining <= 0) break;
    const tierStart = Math.max(seatIndex, tier.min);
    const tierEnd = Math.min(seatIndex + remaining - 1, tier.max);
    const seatsInTier = tierEnd - tierStart + 1;

    if (seatsInTier > 0 && seatIndex <= tier.max) {
      const pricePerSeat = billingCycle === "monthly" ? tier.monthlyPrice : tier.yearlyPrice;
      total += seatsInTier * pricePerSeat;
      remaining -= seatsInTier;
      seatIndex = tierEnd + 1;
    }
  }

  if (billingCycle === "yearly") {
    total *= 12;
  }

  return total;
}

export function TeamBillingReadOnly() {
  const { team, members, loading } = useTeam();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-sm text-muted-foreground">Loading team billing data...</span>
      </div>
    );
  }

  if (!team) return null;

  const seatUsagePercent = team.seat_count > 0 ? (members.length / team.seat_count) * 100 : 0;
  const isActive = team.subscription_status === "active";
  const billingCycle = (team.billing_cycle as "monthly" | "yearly") || "monthly";
  const billingLabel = billingCycle === "yearly" ? "/year" : "/month";
  const currentTotal = calculateTeamTotal(team.seat_count, billingCycle);

  return (
    <ScrollArea className="h-[calc(85vh-10rem)] md:h-[calc(600px-8rem)] pr-2 md:pr-4">
      <div className="space-y-0">
        {/* Info banner */}
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 mb-4">
          <Info className="h-4 w-4 text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground">
            Billing is managed by the team owner. Contact them for plan changes.
          </p>
        </div>

        {/* Plan Header */}
        <div className="flex items-center justify-between py-3 border-b">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium">Team Plan</h3>
              <Badge variant={isActive ? "default" : "secondary"} className={isActive ? "bg-primary" : ""}>
                {isActive ? "Active" : (team.subscription_status || "Inactive")}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Shared access for your entire team
            </p>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold">
              SAR {currentTotal}
              <span className="text-sm text-muted-foreground font-normal"> {billingLabel}</span>
            </p>
          </div>
        </div>

        {/* Seats Usage */}
        <div className="pt-4 pb-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Seat Usage</h4>
        </div>
        <div className="py-3 border-b space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Seats</span>
            </div>
            <span className="text-muted-foreground">
              {members.length} / {team.seat_count} used
            </span>
          </div>
          <Progress value={seatUsagePercent} className="h-2" />
          <p className="text-xs text-muted-foreground">
            {team.seat_count - members.length} seat{team.seat_count - members.length !== 1 ? "s" : ""} available
          </p>
        </div>

        {/* Subscription Details */}
        <div className="pt-4 pb-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Subscription Details</h4>
        </div>
        <div className="space-y-0">
          <div className="flex items-center justify-between py-3 border-b">
            <span className="text-sm font-medium">Billing Cycle</span>
            <span className="text-sm text-muted-foreground capitalize">{billingCycle}</span>
          </div>
          <div className="flex items-center justify-between py-3 border-b">
            <span className="text-sm font-medium">Next Billing Date</span>
            <span className="text-sm text-muted-foreground">
              {team.subscription_period_end
                ? new Date(team.subscription_period_end).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : "—"}
            </span>
          </div>
        </div>

        {/* Usage & Limits */}
        <div className="pt-4 pb-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Usage & Limits</h4>
        </div>
        <div className="space-y-0">
          {["Uploads", "AI Chats", "Exams", "Flashcards", "Quizzes"].map((item) => (
            <div key={item} className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium">{item}</span>
              <span className="text-xs text-muted-foreground font-extrabold">Unlimited</span>
            </div>
          ))}
        </div>
      </div>
    </ScrollArea>
  );
}
